import { useLazyQuery, useSubscription } from '@apollo/client';
import { Badge, Popover } from 'antd';
import AppNotification from 'components/AppNotification';
import SweetAlertAsModal from 'components/common/SweetAlertAsModal';
import NotaryAppointmentNotification from 'components/NotaryAppointmentNotification';
import { INotaryAppointments } from 'components/NotaryAppointmentNotification/types';
import { NOTIFY } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NotificationAction from 'redux/notification/notification.action';
import notificationSelector from 'redux/notification/notification.selector';
import { INotification, NotificationSubType } from 'redux/notification/types';
import { GET_NOTIFICATIONS } from 'services/graphql/queries/notification.query';
import { NOTIFICATION_SUB } from 'services/graphql/subscriptions/notification.subscription';
import { notifyEvent } from 'utils/realtimeUpdate';

const UserNotifications: React.FC = () => {
  const user = useCurrentLoggedInUser(); // test nha
  const notificationVisible = useSelector(notificationSelector.isOpen);
  const unreadNotifications = useSelector(notificationSelector.getUnreadNotifications);
  const dispatch = useDispatch();
  const { data: subData } = useSubscription(NOTIFICATION_SUB, {
    variables: {
      userId: user.id,
    },
  });
  const [notificationQuery, { data }] = useLazyQuery(GET_NOTIFICATIONS);
  const [notificationAlert, setNotificationAlert] = useState({
    isVisible: false,
    title: '',
    description: '',
    link: '',
  });

  const [assignmentNotification, setAssignmentNotification] = useState<{
    visible: boolean;
    notaryAppointment?: INotaryAppointments;
  }>({
    visible: false,
    notaryAppointment: undefined,
  });

  const history = useHistory();

  async function getNotifications() {
    notificationQuery({
      variables: {
        userId: user.id,
      },
    });
  }

  const onNotificationOk = () => {
    history.push(notificationAlert.link);
    setNotificationAlert({
      isVisible: false,
      title: '',
      description: '',
      link: '',
    });
  };

  const onNotificationCancel = () => {
    setNotificationAlert({
      isVisible: false,
      title: '',
      description: '',
      link: '',
    });
  };

  const checkAppointmentNotification = (notificationAppData: INotification) => {
    switch (notificationAppData.subType) {
      case NotificationSubType.APPOINTMENT_APPROVED:
        setNotificationAlert({
          isVisible: true,
          title: notificationAppData.title,
          description: notificationAppData.description,
          link: `${notificationAppData.link as string}&tab=all`,
        });
        break;
      case NotificationSubType.APPOINTMENT_ASSIGNED:
        setAssignmentNotification({ visible: true, notaryAppointment: notificationAppData.data?.detail });
        break;
      case NotificationSubType.APPOINTMENT_REJECTED:
        setNotificationAlert({
          isVisible: true,
          title: notificationAppData.title,
          description: notificationAppData.description,
          link: `${notificationAppData.link as string}&tab=all`,
        });
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    if (user.id) {
      getNotifications();
    }
  }, [user.id]);

  React.useEffect(() => {
    if (data) {
      if (Array.isArray(data?.getNotifications)) {
        dispatch(NotificationAction.fetchNotification(data?.getNotifications));
      }
    }
  }, [data]);

  React.useEffect(() => {
    if (subData && subData.newNotification) {
      notifyEvent.emit(NOTIFY.NEW, subData);
      dispatch(NotificationAction.newNotification(subData.newNotification));
      checkAppointmentNotification(subData.newNotification);
    }
  }, [subData]);

  const toggleVisibleChange = React.useCallback(() => {
    dispatch(NotificationAction.toggleNotification());
  }, []);

  const closeNotaryAppointmentNotificationModal = useCallback(() => {
    setAssignmentNotification({ visible: false, notaryAppointment: undefined });
  }, []);

  return (
    <>
      <>
        <li className='gx-notify'>
          <Popover
            overlayClassName='gx-popover-horizantal'
            placement='bottomRight'
            content={<AppNotification />}
            onVisibleChange={toggleVisibleChange}
            visible={notificationVisible}
            trigger='click'>
            <div className='gx-pt-2'>
              <Badge count={unreadNotifications.length} size='small'>
                <span className='gx-pointer gx-d-block'>
                  <i style={{ fontSize: '20px' }} className='icon icon-notification' />
                </span>
              </Badge>
            </div>
          </Popover>
        </li>

        {/* <li className='gx-msg'>
    <Popover
      overlayClassName='gx-popover-horizantal'
      placement='bottomRight'
      content={<MailNotification />}
      trigger='click'>
      <span className='gx-pointer gx-status-pos gx-d-block'>
        <i className='icon icon-chat-new' />
        <span className='gx-status gx-status-rtl gx-small gx-orange' />
      </span>
    </Popover>
  </li> */}
      </>
      <SweetAlertAsModal
        show={notificationAlert.isVisible}
        info
        showCancel
        confirmBtnText='Detail'
        cancelBtnText='Ignore'
        title={notificationAlert.title}
        onConfirm={onNotificationOk}
        onCancel={onNotificationCancel}
        focusConfirmBtn>
        {notificationAlert.description}
      </SweetAlertAsModal>
      <NotaryAppointmentNotification
        closeModal={closeNotaryAppointmentNotificationModal}
        visible={assignmentNotification.visible}
        notaryAppointment={assignmentNotification.notaryAppointment}
      />
    </>
  );
};

export default React.memo(UserNotifications);
